import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ExitIntentPopup from '../../components/feature/ExitIntentPopup';
import SocialProofBanner from '../../components/feature/SocialProofBanner';
import SubscriberCounter from '../../components/feature/SubscriberCounter';
import { supabase } from '../../lib/supabase';
import { trackCTAClick, trackAIAssistant } from '../../lib/analytics';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const navigate = useNavigate();

  const handleNewsletterSubscribe = async (email: string) => {
    try {
      const { error } = await supabase
        .from('newsletter_subscribers')
        .insert([{ 
          email,
          source: 'exit_intent_popup',
          subscribed_at: new Date().toISOString()
        }]);

      if (error && error.code !== '23505') {
        throw error;
      }

      await supabase.functions.invoke('send-welcome-email', {
        body: { email }
      });
    } catch (error) {
      console.error('Error subscribing:', error);
      throw error;
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchCategoryCounts = async () => {
      setLoadingCounts(true);
      const { data, error } = await supabase
        .from('listings')
        .select('category')
        .eq('status', 'approved');

      if (!error && data) {
        const counts: Record<string, number> = {};
        data.forEach((item) => {
          counts[item.category] = (counts[item.category] || 0) + 1;
        });
        setCategoryCounts(counts);
      }
      setLoadingCounts(false);
    };

    fetchCategoryCounts();
  }, []);

  useEffect(() => {
    document.title = 'Ireland Bound - Your Guide to Moving from USA to Ireland';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Complete guide and community for Americans moving to Ireland. Find jobs, housing, visa information, and connect with fellow expats.');
    }
  }, []);

  const openAIAssistant = () => {
    trackAIAssistant('open', { source: 'homepage' });
    const assistantButton = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (assistantButton) {
      assistantButton.click();
    }
  };

  const handleCTAClick = (ctaName: string, destination: string) => {
    trackCTAClick(ctaName, 'homepage');
  };

  return (
    <>
      <div className="min-h-screen bg-white">
        <Navbar scrolled={scrolled} />
        
        {/* Original Hero Section with Cliffs of Moher */}
        <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/38c2932f20b2f2936d47bc0cd41dabed.jpg"
              alt="Cliffs of Moher Ireland"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          </div>

          {/* Hero Content */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full py-20 animate-fade-in">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Your Journey to Ireland<br />Starts Here
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-10 max-w-3xl mx-auto">
              Expert services, community support, and practical tools all in one place
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="/directory"
                onClick={() => handleCTAClick('Find Services Hero', '/directory')}
                className="bg-[#0A381F] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#0A381F]/90 transition-all hover-lift whitespace-nowrap"
              >
                <i className="ri-search-line mr-2"></i>
                Find Services
              </a>
              <a
                href="/community"
                onClick={() => handleCTAClick('Join Community Hero', '/community')}
                className="bg-white text-[#0A381F] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all hover-lift whitespace-nowrap"
              >
                <i className="ri-group-line mr-2"></i>
                Join Community
              </a>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <i className="ri-arrow-down-line text-white text-3xl"></i>
          </div>
        </div>

        {/* Relocation Expert Section - Prominent Feature Below Hero */}
        <div className="py-20 md:py-32 bg-gradient-to-br from-[#0A381F]/5 via-emerald-50/30 to-[#0A381F]/5 relative overflow-hidden">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 left-10 w-64 h-64 md:w-96 md:h-96 bg-[#0A381F]/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-10 w-72 h-72 md:w-[500px] md:h-[500px] bg-[#D4AF37]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Header */}
            <div className="text-center mb-12 md:mb-16 animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-[#0A381F]/10 text-[#0A381F] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-sparkling-2-fill text-lg"></i>
                Expert Relocation Guidance
              </div>
              <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4 md:mb-6 leading-tight px-4">
                Meet Your Personal<br />
                <span className="bg-gradient-to-r from-[#0A381F] to-[#D4AF37] bg-clip-text text-transparent">
                  Ireland Relocation Expert
                </span>
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
                Get instant answers about visas, housing, jobs, healthcare, and everything you need to know about moving to Ireland. Available 24/7 via chat or voice.
              </p>
            </div>

            {/* Interactive Relocation Expert Card */}
            <div className="max-w-5xl mx-auto mb-12 md:mb-16 animate-scale-in">
              <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
                {/* Card Header */}
                <div className="bg-gradient-to-r from-[#0A381F] to-[#0D4A28] px-4 md:px-8 py-4 md:py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-12 h-12 md:w-14 md:h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-star-fill text-2xl md:text-3xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-xl md:text-2xl font-bold text-white">Ireland Relocation Expert</h3>
                      <p className="text-white/80 text-xs md:text-sm">Expert guidance • Always available</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 md:px-4 py-2 rounded-full">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-white text-sm font-semibold whitespace-nowrap">Online Now</span>
                  </div>
                </div>

                {/* Sample Conversation */}
                <div className="p-4 md:p-8 bg-gray-50 space-y-3 md:space-y-4 max-h-80 md:max-h-96 overflow-y-auto">
                  {/* User Message */}
                  <div className="flex justify-end animate-slide-in-right">
                    <div className="bg-[#0A381F] text-white px-4 md:px-6 py-2 md:py-3 rounded-2xl rounded-tr-sm max-w-[85%] md:max-w-md">
                      <p className="text-sm md:text-base">What visa do I need to work in Ireland as a software engineer?</p>
                    </div>
                  </div>

                  {/* AI Response */}
                  <div className="flex justify-start animate-slide-in-left" style={{ animationDelay: '0.3s' }}>
                    <div className="bg-white border border-gray-200 px-4 md:px-6 py-2 md:py-3 rounded-2xl rounded-tl-sm max-w-[90%] md:max-w-lg shadow-sm">
                      <p className="text-sm md:text-base text-gray-800">
                        For software engineers, you'll likely need a <strong>Critical Skills Employment Permit</strong>. This is available for roles paying €30,000+ annually. The process takes 8-12 weeks and requires a job offer from an Irish employer. I can guide you through the entire application process!
                      </p>
                    </div>
                  </div>

                  {/* User Message */}
                  <div className="flex justify-end animate-slide-in-right" style={{ animationDelay: '0.6s' }}>
                    <div className="bg-[#0A381F] text-white px-4 md:px-6 py-2 md:py-3 rounded-2xl rounded-tr-sm max-w-[85%] md:max-w-md">
                      <p className="text-sm md:text-base">How much does it cost to rent in Dublin?</p>
                    </div>
                  </div>

                  {/* AI Response */}
                  <div className="flex justify-start animate-slide-in-left" style={{ animationDelay: '0.9s' }}>
                    <div className="bg-white border border-gray-200 px-4 md:px-6 py-2 md:py-3 rounded-2xl rounded-tl-sm max-w-[90%] md:max-w-lg shadow-sm">
                      <p className="text-sm md:text-base text-gray-800">
                        Dublin rent averages €1,800-€2,200/month for a 1-bedroom apartment in the city center. Outside the center, expect €1,400-€1,700. I can help you find affordable neighborhoods and connect you with trusted housing agents!
                      </p>
                    </div>
                  </div>
                </div>

                {/* CTA Section */}
                <div className="p-4 md:p-8 bg-white border-t border-gray-100">
                  <div className="flex flex-col sm:flex-row gap-3 md:gap-4 items-stretch sm:items-center justify-center">
                    <button
                      onClick={() => {
                        trackCTAClick('Start Talking Now', 'relocation_expert_card');
                        openAIAssistant();
                      }}
                      className="bg-gradient-to-r from-[#0A381F] to-[#0D4A28] text-white px-6 md:px-10 py-4 md:py-5 rounded-xl text-base md:text-lg font-bold hover:shadow-xl transition-all hover-lift flex items-center justify-center gap-2 md:gap-3 whitespace-nowrap group"
                    >
                      <i className="ri-chat-voice-fill text-xl md:text-2xl"></i>
                      Start Talking Now
                      <i className="ri-arrow-right-line text-lg md:text-xl group-hover:translate-x-1 transition-transform"></i>
                    </button>
                    <button
                      onClick={() => {
                        trackCTAClick('Chat via Text', 'relocation_expert_card');
                        openAIAssistant();
                      }}
                      className="bg-white text-[#0A381F] px-6 md:px-10 py-4 md:py-5 rounded-xl text-base md:text-lg font-bold border-2 border-[#0A381F] hover:bg-[#0A381F]/5 transition-all hover-lift flex items-center justify-center gap-2 md:gap-3 whitespace-nowrap"
                    >
                      <i className="ri-message-3-fill text-xl md:text-2xl"></i>
                      Chat via Text
                    </button>
                  </div>
                  <p className="text-center text-gray-500 text-xs md:text-sm mt-4">
                    Free to use • No registration required • Instant responses
                  </p>
                </div>
              </div>
            </div>

            {/* Feature Pills */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-5xl mx-auto animate-fade-in" style={{ animationDelay: '1.2s' }}>
              {[
                { icon: 'ri-24-hours-line', text: 'Available 24/7' },
                { icon: 'ri-voice-recognition-line', text: 'Voice & Chat' },
                { icon: 'ri-global-line', text: 'Expert Knowledge' },
                { icon: 'ri-shield-check-line', text: 'Trusted Advice' },
              ].map((feature, index) => (
                <div key={index} className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-xl px-3 md:px-4 py-3 flex flex-col sm:flex-row items-center gap-2 md:gap-3 hover:shadow-lg transition-all hover-lift">
                  <div className="w-10 h-10 bg-[#0A381F]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${feature.icon} text-lg md:text-xl text-[#0A381F]`}></i>
                  </div>
                  <span className="font-semibold text-gray-800 text-xs md:text-sm text-center sm:text-left">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* What the Relocation Expert Can Help With Section */}
        <div className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16 animate-fade-in">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Your Relocation Expert Knows Everything About Ireland
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
                Get expert guidance on every aspect of your relocation journey
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {[
                {
                  icon: 'ri-passport-line',
                  title: 'Visas & Immigration',
                  topics: ['Work permits', 'Student visas', 'Family reunification', 'Citizenship pathways'],
                  color: '[#0A381F]',
                  details: 'Navigate Ireland\'s immigration system with confidence. Get guidance on Critical Skills Employment Permits, General Employment Permits, student visas, and family reunification processes. Learn about processing times, required documents, and eligibility criteria for each visa type.',
                  cta: 'Ask about your visa options',
                },
                {
                  icon: 'ri-home-heart-line',
                  title: 'Housing & Living',
                  topics: ['Rental market tips', 'Neighborhood guides', 'Utilities setup', 'Cost of living'],
                  color: '[#D4AF37]',
                  details: 'Find your perfect home in Ireland. Discover the best neighborhoods for your lifestyle and budget, understand rental agreements, learn how to set up utilities, and get realistic cost of living estimates for different cities across Ireland.',
                  cta: 'Explore housing options',
                },
                {
                  icon: 'ri-briefcase-line',
                  title: 'Jobs & Career',
                  topics: ['Job market insights', 'Salary expectations', 'CV formatting', 'Interview prep'],
                  color: '[#0A381F]',
                  details: 'Launch your career in Ireland\'s thriving job market. Get insights into in-demand industries, understand salary ranges for your profession, learn how to format your CV for Irish employers, and prepare for interviews with local companies.',
                  cta: 'Get career advice',
                },
                {
                  icon: 'ri-heart-pulse-line',
                  title: 'Healthcare System',
                  topics: ['Medical card', 'GP registration', 'Health insurance', 'Emergency services'],
                  color: '[#D4AF37]',
                  details: 'Understand Ireland\'s healthcare system. Learn how to register with a GP, apply for a medical card, choose the right health insurance, and access emergency services. Get guidance on prescription costs and specialist referrals.',
                  cta: 'Learn about healthcare',
                },
                {
                  icon: 'ri-bank-line',
                  title: 'Banking & Finance',
                  topics: ['Opening accounts', 'Tax system (PAYE)', 'PPS number', 'Currency exchange'],
                  color: '[#0A381F]',
                  details: 'Set up your financial life in Ireland. Discover how to open a bank account, apply for your PPS number (essential for work and taxes), understand the PAYE tax system, and get tips on currency exchange and international transfers.',
                  cta: 'Ask about banking',
                },
                {
                  icon: 'ri-graduation-cap-line',
                  title: 'Education & Culture',
                  topics: ['School enrollment', 'Language courses', 'Irish culture', 'Social integration'],
                  color: '[#D4AF37]',
                  details: 'Integrate into Irish life and culture. Learn about enrolling children in schools, find English or Irish language courses, understand Irish customs and traditions, and discover ways to build your social network and feel at home.',
                  cta: 'Discover Irish culture',
                },
              ].map((category, index) => (
                <div
                  key={index}
                  className={`bg-gray-50 rounded-2xl hover:shadow-xl transition-all group animate-scale-in border border-gray-100 cursor-pointer overflow-hidden ${
                    expandedCard === index ? 'ring-2 ring-[#0A381F]' : ''
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onClick={() => setExpandedCard(expandedCard === index ? null : index)}
                >
                  <div className="p-6 md:p-8">
                    <div className="flex items-start justify-between mb-4 md:mb-6">
                      <div className={`w-14 h-14 md:w-16 md:h-16 bg-${category.color}/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0`}>
                        <i className={`${category.icon} text-2xl md:text-3xl text-${category.color}`}></i>
                      </div>
                      <div className={`w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm transition-transform ${
                        expandedCard === index ? 'rotate-180' : ''
                      }`}>
                        <i className="ri-arrow-down-s-line text-gray-600 text-xl"></i>
                      </div>
                    </div>
                    
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">{category.title}</h3>
                    
                    <ul className="space-y-2 mb-4">
                      {category.topics.map((topic, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm md:text-base text-gray-600">
                          <i className="ri-check-line text-[#0A381F] flex-shrink-0"></i>
                          <span>{topic}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Expanded Content */}
                    <div
                      className={`transition-all duration-300 ease-in-out ${
                        expandedCard === index
                          ? 'max-h-96 opacity-100 mt-4'
                          : 'max-h-0 opacity-0 overflow-hidden'
                      }`}
                    >
                      <div className="pt-4 border-t border-gray-200">
                        <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-4">
                          {category.details}
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            trackCTAClick(category.cta, 'expertise_cards');
                            openAIAssistant();
                          }}
                          className={`w-full bg-gradient-to-r from-${category.color} to-${category.color}/80 text-white px-4 py-3 rounded-lg text-sm md:text-base font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2 whitespace-nowrap`}
                        >
                          <i className="ri-chat-3-line text-lg"></i>
                          {category.cta}
                        </button>
                      </div>
                    </div>

                    {/* Click to expand hint */}
                    {expandedCard !== index && (
                      <div className="text-center mt-2">
                        <span className="text-xs text-gray-400 font-medium">Click to learn more</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-8 md:mt-12">
              <button
                onClick={() => {
                  trackCTAClick('Ask Your Question Now', 'expertise_section_bottom');
                  openAIAssistant();
                }}
                className="bg-gradient-to-r from-[#0A381F] to-[#0D4A28] text-white px-6 md:px-8 py-3 md:py-4 rounded-xl text-base md:text-lg font-bold hover:shadow-xl transition-all hover-lift inline-flex items-center gap-2 md:gap-3 whitespace-nowrap"
              >
                <i className="ri-question-answer-line text-xl md:text-2xl"></i>
                Ask Your Question Now
              </button>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="py-16 md:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16 animate-fade-in">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Everything You Need in One Place
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
                Comprehensive resources and support for every step of your relocation journey
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {[
                {
                  icon: 'ri-passport-line',
                  title: 'Irish Citizenship',
                  description: 'Claim your Irish citizenship through the grandparent rule and unlock EU benefits',
                  link: '/citizenship',
                  color: '[#D4AF37]',
                  badge: 'Popular',
                },
                {
                  icon: 'ri-building-line',
                  title: 'Service Directory',
                  description: 'Find trusted immigration lawyers, housing agents, and relocation services',
                  link: '/directory',
                  color: '[#0A381F]',
                },
                {
                  icon: 'ri-group-line',
                  title: 'Community Hub',
                  description: 'Connect with others, share experiences, and get advice from people who have been there',
                  link: '/community',
                  color: '[#D4AF37]',
                },
                {
                  icon: 'ri-tools-line',
                  title: 'Practical Tools',
                  description: 'Calculators, checklists, and guides to help you plan and budget your move',
                  link: '/tools',
                  color: '[#0A381F]',
                },
              ].map((feature, index) => (
                <a
                  key={index}
                  href={feature.link}
                  onClick={() => handleCTAClick(feature.title, 'features_section')}
                  className="bg-white p-6 md:p-8 rounded-xl shadow-sm hover:shadow-xl transition-all hover-lift group animate-scale-in relative"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {feature.badge && (
                    <div className="absolute top-4 right-4 bg-[#D4AF37] text-white text-xs font-bold px-3 py-1 rounded-full">
                      {feature.badge}
                    </div>
                  )}
                  <div className={`w-14 h-14 md:w-16 md:h-16 bg-${feature.color}/10 rounded-lg flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 transition-transform`}>
                    <i className={`${feature.icon} text-2xl md:text-3xl text-${feature.color}`}></i>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-sm md:text-base text-gray-600 mb-4">{feature.description}</p>
                  <span className={`text-${feature.color} font-semibold group-hover:gap-2 flex items-center gap-1 transition-all whitespace-nowrap text-sm md:text-base`}>
                    Learn More <i className="ri-arrow-right-line"></i>
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16 animate-fade-in">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                How It Works
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
                Your step-by-step guide to a successful relocation
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {[
                { step: '1', title: 'Plan', description: 'Use our tools to calculate costs and create your relocation checklist', icon: 'ri-file-list-3-line' },
                { step: '2', title: 'Connect', description: 'Join our community and learn from others who have made the move', icon: 'ri-user-add-line' },
                { step: '3', title: 'Find Services', description: 'Browse verified providers for visas, housing, and more', icon: 'ri-search-2-line' },
                { step: '4', title: 'Relocate', description: 'Execute your plan with confidence and ongoing support', icon: 'ri-flight-takeoff-line' },
              ].map((step, index) => (
                <div key={index} className="text-center animate-scale-in" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="relative mb-4 md:mb-6">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                      <i className={`${step.icon} text-2xl md:text-3xl text-teal-600`}></i>
                    </div>
                    <div className="absolute -top-2 -right-2 left-1/2 transform translate-x-4 md:translate-x-6 w-8 h-8 md:w-10 md:h-10 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold text-base md:text-lg">
                      {step.step}
                    </div>
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">{step.title}</h3>
                  <p className="text-sm md:text-base text-gray-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=vibrant%20abstract%20flowing%20waves%20gradient%20from%20teal%20to%20emerald%20modern%20digital%20background%20with%20smooth%20curves%20and%20dynamic%20movement%20professional%20contemporary%20design%20bright%20and%20energetic%20atmosphere&width=1920&height=600&seq=cta-bg-001&orientation=landscape"
              alt="CTA Background"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-teal-900/80 to-emerald-900/80"></div>
          </div>

          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 md:mb-6 animate-fade-in">
              Ready to Start Your Irish Adventure?
            </h2>
            <p className="text-lg md:text-xl text-white/90 mb-8 md:mb-10 animate-fade-in px-4">
              Join thousands of people who have successfully relocated to Ireland with our help
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center animate-bounce-in px-4">
              <button
                onClick={() => {
                  trackCTAClick('Talk to Relocation Expert Final', 'final_cta');
                  openAIAssistant();
                }}
                className="bg-white text-[#0A381F] px-6 md:px-8 py-3 md:py-4 rounded-lg text-base md:text-lg font-semibold hover:bg-gray-50 transition-all hover-lift whitespace-nowrap"
              >
                <i className="ri-user-star-line mr-2"></i>
                Talk to Relocation Expert
              </button>
              <a
                href="/directory"
                onClick={() => handleCTAClick('Explore Services Final', '/directory')}
                className="bg-[#D4AF37] text-white px-6 md:px-8 py-3 md:py-4 rounded-lg text-base md:text-lg font-semibold hover:bg-[#C19B2F] transition-all hover-lift whitespace-nowrap border-2 border-white/20"
              >
                <i className="ri-compass-3-line mr-2"></i>
                Explore Services
              </a>
            </div>
          </div>
        </div>

        <ExitIntentPopup onSubscribe={handleNewsletterSubscribe} />
        <SocialProofBanner />
        <SubscriberCounter />

        <Footer />
      </div>
    </>
  );
}
